/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { blobContainerSwaggerToBlobContainerStore, blobSwaggerToAzureBlobStore } from 'src/core/handlers/azure/azureMappers';
import { getAzureBlobService } from 'src/core/services/azure/azureBlobService';
import { getAzureContainerService } from 'src/core/services/azure/azureContainerService';
import { ChunkedAzureData } from 'src/core/services/azure/azureService';
import { AzureBlobSwagger, AzureContainerSwagger } from 'src/core/services/azure/azureSwaggers';
import { AzureBlobContainerStore } from 'src/core/stores/AzureBlobContainerStore';
import { AzureBlobStore } from 'src/core/stores/AzureBlobStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';

export type AzureContainerHandlerGroup = ReturnType<typeof createAzureContainerHandlerGroup>;

const sortByName = <T extends { name: string }>(data: T[]) =>
    data.sort((a, b) => (a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase() ? 1 : -1));

/**
 * Creates a handler group of functions for Azure container service to
 * be fired by the dispatcher.
 *
 * @param dependencies The dependencies required by this factory.
 */
export const createAzureContainerHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;

    const azureBlobService = getAzureBlobService();
    const azureContainerService = getAzureContainerService();

    /**
     * Loads azure container(s) for specific storage account.
     */
    const getAzureContainersByStorageAccountName = async ({
        name,
        localStore,
        subscriptionId,
        resourceGroupName
    }: HandlerParams<
        Pick<AzureBlobContainerStore, 'name'> & { localStore: ResourceStoreHouse<AzureBlobContainerStore> } & {
            subscriptionId: string;
            resourceGroupName: string;
        }
    >) => {
        const userToken = rootStore.azureStore.session.userTokens.armToken;

        let chunkedUserAzureBlobContainersSwagger: ChunkedAzureData<AzureContainerSwagger> = { data: [], error: undefined };

        localStore.tracker.setState('running');

        chunkedUserAzureBlobContainersSwagger = await azureContainerService.getAzureContainersByStorageAccountName({
            name,
            subscriptionId,
            token: userToken,
            resourceGroupName
        });

        const blobContainerStores = [...chunkedUserAzureBlobContainersSwagger.data.map(blobContainerSwaggerToBlobContainerStore)].filter(
            (blobContainer, index, arr) => arr.findIndex(s => s.id === blobContainer.id) === index
        );

        localStore.setItems(sortByName(blobContainerStores));
        localStore.tracker.setState('completed');
    };

    /**
     * List azure blobs for the given container name.
     */
    const getAzureBlobsByContainerName = async ({
        localStore,
        containerName,
        storageAccountName
    }: HandlerParams<{
        containerName: string;
        storageAccountName: string;
        localStore: ResourceStoreHouse<AzureBlobStore>;
    }>) => {
        const { session } = rootStore.azureStore;
        const { storageToken } = session.userTokens;

        localStore.tracker.setState('running');

        const blobsSwagger: AzureBlobSwagger[] = await azureBlobService.getAzureBlobsByContainerName({
            storageToken,
            storageAccountName,
            containerName
        });

        localStore.setItems(blobsSwagger.map(blobSwaggerToAzureBlobStore));
        localStore.tracker.setState('completed');
    };

    const checkStorageCORS = async ({ storageAccountName }: HandlerParams<{ storageAccountName: string }>) => {
        const { session } = rootStore.azureStore;
        const { storageToken } = session.userTokens;

        const isEnabled = await azureContainerService.checkStorageCORS({ storageAccountName, storageToken });

        if (!isEnabled) {
            throw new Error(`The CORS policy required for accessing ${storageAccountName} is disabled!`);
        }
    };

    const checkAndEnableStorageCORS = async ({ storageAccountName }: HandlerParams<{ storageAccountName: string }>) => {
        const userToken = rootStore.azureStore.session.userTokens.storageToken;

        try {
            await checkStorageCORS({ storageAccountName });
        } catch {
            await azureContainerService.setAndCheckAzureBlobStorageCORSPolicies({ storageAccountName, storageToken: userToken });
        }
    };

    return { checkStorageCORS, getAzureBlobsByContainerName, getAzureContainersByStorageAccountName, checkAndEnableStorageCORS };
};
